// src/components/NummiusLoader/NummiusLoader.jsx
// Nummius brand loader: brand mark + label above a scrolling candlestick
// chart. The single loading indicator for the whole app - replaces the old
// MUI CircularProgress/LinearProgress spinners and "Loading..." text.
//
// variant="page"   - fixed, full-viewport overlay (app boot, auth check,
//                    route changes, lazy routes, whole-page first loads)
// variant="block"  - fills its parent and centers itself (panels, charts,
//                    tables, modals, tabs); min-height 160px. With `fill`,
//                    it takes the visible page height instead, so a whole
//                    page's content loading inside the /app shell sits in
//                    the middle of the screen (nav/header stay visible)
// variant="inline" - mark + label only, no chart (buttons, small cells,
//                    inline refreshes)
//
// Theme follows the app's MUI color scheme (the data-mui-color-scheme
// attribute MUI's CssVarsProvider puts on <html>, which public/index.html's
// pre-paint script also sets from the saved 'mode' before first paint), and
// only falls back to prefers-color-scheme when no scheme is set at all.
// Force it with theme="light" | "dark".
//
// The markup here is mirrored by the boot loader in public/index.html -
// keep the two in sync.
import React, { useEffect, useId, useState } from 'react';
import './nummius-loader.css';

/* ---- candle geometry (deterministic, loops seamlessly) ---- */
const N = 20, H = 220, K = 2.5, STEP = 16, TAU = Math.PI * 2;
const series = Array.from({ length: N + 1 }, (_, i) =>
  48 + 18 * Math.sin((i / N) * TAU) + 7 * Math.sin((i / N) * TAU * 5 + 1) + 3 * Math.cos((i / N) * TAU * 3)
);
const y = (v) => +(H - v * K).toFixed(1);
const CANDLES = Array.from({ length: N }, (_, i) => {
  const o = series[i], c = series[i + 1];
  const top = Math.max(o, c), bot = Math.min(o, c);
  const hi = top + 3 + (i % 3) * 2, lo = bot - 3 - ((i * 7) % 4);
  const bodyH = Math.max(1.6, top - bot) * K;
  return { x: i * STEP, up: c >= o, hi: y(hi), top: y(top), bot: +(y(top) + bodyH).toFixed(1), lo: y(lo) };
});

function Candles() {
  return (
    <svg className="nm-candles" viewBox="0 4 320 188" aria-hidden="true" focusable="false">
      {CANDLES.map((d) => (
        <g key={d.x}>
          <line x1={d.x + 5} y1={d.hi} x2={d.x + 5} y2={d.top} />
          <line x1={d.x + 5} y1={d.bot} x2={d.x + 5} y2={d.lo} />
          <rect className={d.up ? 'up' : 'dn'} x={d.x + 0.75} y={d.top} width={8.5} height={+(d.bot - d.top).toFixed(1)} rx={1.5} />
        </g>
      ))}
    </svg>
  );
}

/** The Nummius N with candles. The outlined candle is a true cutout (transparent). */
export function NummiusMark({ className }) {
  const id = 'nm-cut-' + useId().replace(/[^a-zA-Z0-9_-]/g, '');
  return (
    <svg className={className} viewBox="195 358 410 472" aria-hidden="true" focusable="false">
      <defs>
        <mask id={id} maskUnits="userSpaceOnUse" x="195" y="358" width="410" height="472">
          <rect x="195" y="358" width="410" height="472" fill="#fff" />
          <rect x="440" y="588" width="6" height="164" rx="3" fill="#000" />
          <rect x="420" y="620" width="46" height="90" rx="4" fill="#000" />
        </mask>
      </defs>
      <g fill="currentColor">
        <g mask={`url(#${id})`}>
          <path d="M203 817V530C245 540 280 590 310 655V817Z" />
          <path d="M203 500V478C203 434 238 405 282 405C314 405 334 423 350 445L508 680C530 712 565 718 597 710V745C597 790 560 822 515 822C481 822 457 802 440 778L300 560C275 522 245 494 203 500Z" />
        </g>
        <rect x="504" y="441" width="6" height="250" rx="3" />
        <rect x="487" y="498" width="39" height="113" rx="4" />
        <rect x="559" y="366" width="6" height="309" rx="3" />
        <rect x="542" y="409" width="40" height="170" rx="4" />
      </g>
    </svg>
  );
}

export default function NummiusLoader({
  variant = 'block',
  label = 'Loading market data',
  theme = 'auto',
  /** Wait this long before showing, so fast loads never flash. */
  delayMs = 200,
  /** Inline only: keep the label for screen readers but show just the mark
   *  (icon-sized slots like a search adornment or a button's startIcon). */
  hideLabel = false,
  /** Block only: fill the visible page height (whole-page content loads). */
  fill = false,
  className = '',
}) {
  const [visible, setVisible] = useState(delayMs <= 0);
  useEffect(() => {
    if (delayMs <= 0) return undefined;
    const t = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);

  return (
    <div
      className={`nm-loader nm-loader--${variant}${hideLabel ? ' nm-loader--mark-only' : ''}${fill ? ' nm-loader--fill' : ''} ${className}`}
      data-nm-theme={theme === 'auto' ? undefined : theme}
      data-visible={visible || undefined}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="nm-loader__inner">
        <div className="nm-loader__head">
          <NummiusMark className="nm-loader__mark" />
          <span className="nm-loader__label">{label}</span>
          <span className="nm-loader__dot" aria-hidden="true" />
        </div>
        {variant !== 'inline' && (
          <div className="nm-loader__window" aria-hidden="true">
            <div className="nm-loader__track">
              <Candles />
              <Candles />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Button content that swaps to the inline loader while `pending`, without
 * changing the button's width: the normal children stay in the layout
 * (invisible) and the loader is overlaid on top of them. The button itself
 * must still be disabled while pending - this only handles the content.
 *
 * `color` pins the loader's ink while pending. MUI <Button> swaps its text
 * to the faint action.disabled color when disabled, which the inline loader
 * would otherwise inherit - pass the button's normal text color there.
 */
export function PendingLabel({ pending, label, hideLabel = false, color, children }) {
  return (
    <span className="nm-pending" data-pending={pending || undefined} style={pending && color ? { color } : undefined}>
      <span className="nm-pending__content">{children}</span>
      {pending && (
        <NummiusLoader variant="inline" label={label} delayMs={0} hideLabel={hideLabel} className="nm-pending__loader" />
      )}
    </span>
  );
}
